import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { SortDirection } from '@/api/sorting'
import { TableHead } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * A column heading that orders the list.
 *
 * A real `<button>` inside the `<th>` rather than a click handler on the cell: the heading has to
 * be reachable by keyboard, and `aria-sort` on the cell is what tells a screen reader which column
 * the table is in order by and which way round.
 *
 * Ordering happens in the backend — see the sort enumerations there. A column sorted in the page
 * would only ever order the twenty rows it has, which is not what somebody clicking a heading over
 * a list of six hundred leads is asking for.
 */
export function SortableHead<TSort extends string>({
  column,
  sort,
  direction,
  onToggle,
  className,
  children,
}: {
  column: TSort
  sort: TSort
  direction: SortDirection
  /**
   * Which way round the column starts is the list's to know, not the heading's — a date from the
   * newest, a name from A. Keeping it in one table there is what makes a hand-typed `?sort=` mean
   * the same as a click on this heading.
   */
  onToggle: (column: TSort) => void
  className?: string
  children: ReactNode
}) {
  const { t } = useTranslation()
  const active = sort === column
  const ascending = direction === 'ASC'

  return (
    <TableHead
      className={cn('p-0', className)}
      aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onToggle(column)}
        className={cn(
          'group text-muted-foreground hover:text-foreground focus-visible:ring-ring/50',
          'flex h-10 w-full items-center gap-1.5 px-2 text-left font-medium',
          'focus-visible:ring-[3px] focus-visible:outline-none',
          active && 'text-foreground',
        )}
        // The heading already reads the column name; this says what pressing it does.
        title={t('sorting.sortBy')}
      >
        {children}
        {active ? (
          ascending ? (
            <ArrowUpIcon className="size-3.5 shrink-0" aria-hidden />
          ) : (
            <ArrowDownIcon className="size-3.5 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronsUpDownIcon
            className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-50 group-focus-visible:opacity-50"
            aria-hidden
          />
        )}
      </button>
    </TableHead>
  )
}
